/**
 * Condition Walkthrough — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/condition-walkthrough.routes.ts`
 * and `apps/api/src/routes/tenant/condition-walkthrough.routes.ts` import these to register
 * their routes, and `apps/landlord/src/lib/condition.api.ts` /
 * `apps/tenant/src/lib/condition.api.ts` derive their response/body types from the same
 * objects instead of hand-redeclaring them. See packages/api-types/README.md.
 *
 * The manager and tenant route files are near-duplicates of each other (BR-4/5/6/7/7b/8/8d/
 * 17/19, BR-18 photo pipeline) — every schema here is shared verbatim between both routes,
 * with ONE real divergence: `GET .../condition-walkthroughs?phase=&actorRole=` additionally
 * returns `leaseState` on the tenant side (the tenant entry point resolves MOVE_IN vs.
 * MOVE_OUT off lease lifecycle state, not walkthrough existence). `GetForLeaseResponseSchema`
 * is the manager (base) shape; `TenantGetForLeaseResponseSchema` extends it with `leaseState`.
 *
 * Covers:
 *   POST   /landlord|tenant/api/leases/:leaseId/condition-walkthroughs
 *   GET    /landlord|tenant/api/leases/:leaseId/condition-walkthroughs?phase=&actorRole=
 *   GET    /landlord|tenant/api/condition-walkthroughs/:walkthroughId
 *   PATCH  /landlord|tenant/api/condition-walkthroughs/:walkthroughId/items/:itemId
 *   POST   /landlord|tenant/api/condition-walkthroughs/:walkthroughId/submit
 *   POST   /landlord|tenant/api/condition-walkthroughs/:walkthroughId/acknowledge
 *   POST   /landlord|tenant/api/condition-items/:itemId/corrections
 *   POST   /landlord|tenant/api/condition-items/:itemId/photos/upload-url
 *   POST   /landlord|tenant/api/condition-photos/:photoId/confirm
 *   DELETE /landlord|tenant/api/condition-photos/:photoId
 */
import { z } from 'zod';
import { LeaseStateSchema } from '../lease-core/index.js';
// ============================================================================
// ENUMS — must match `packages/database/schema/condition.prisma` exactly.
// ============================================================================
export const WalkthroughPhaseSchema = z.enum(['MOVE_IN', 'MOVE_OUT']);
export const WalkthroughActorRoleSchema = z.enum(['LANDLORD', 'TENANT']);
export const ItemConditionSchema = z.enum(['NOT_ASSESSED', 'OK', 'DEFICIENT']);
export const DeficiencyTypeSchema = z.enum(['DAMAGE', 'STAIN', 'DIRT', 'EXCESSIVE_WEAR', 'MISSING', 'INOPERABLE', 'OTHER']);
export const ItemCategorySchema = z.enum([
    'FLOORING', 'WALLS_CEILING', 'WINDOWS', 'DOORS', 'APPLIANCE', 'FIXTURE',
    'CABINETRY_COUNTERTOP', 'PLUMBING', 'ELECTRICAL', 'HVAC', 'FURNITURE', 'OTHER',
]);
export const PhotoKindSchema = z.enum(['WIDE', 'CLOSEUP', 'GENERAL']);
export const PhotoStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export const CaptureModeSchema = z.enum(['HOLISTIC', 'DEFICIENCY']);
export const WalkthroughStatusSchema = z.enum(['DRAFT', 'SUBMITTED', 'ACKNOWLEDGED']);
// ============================================================================
// REQUEST — query/body schemas shared verbatim between the manager and tenant routes.
// ============================================================================
export const GetForLeaseQuerySchema = z.object({
    phase: WalkthroughPhaseSchema,
    actorRole: WalkthroughActorRoleSchema,
});
export const CreateWalkthroughBodySchema = z.object({
    phase: WalkthroughPhaseSchema,
});
// BR-27/DC-9: a request with NO body at all (the pre-Iteration-4 contract every existing
// submit call still uses) must still validate. Fastify hands the body-less request through
// as `request.body === null` (not `undefined`), and zod's `.default()` only substitutes on
// `undefined` — so a bare object schema (or even `.default({})`) still 400s on `null`. The
// `z.preprocess` normalizes both `null` and `undefined` to `{}` before the inner object
// schema runs, which then applies its own `overridePhotoRequirement` default of `false`.
export const SubmitWalkthroughBodySchema = z.preprocess((val) => val ?? {}, z.object({
    overridePhotoRequirement: z.boolean().optional().default(false),
}));
export const MarkItemBodySchema = z.object({
    condition: ItemConditionSchema,
    deficiencyTypes: z.array(DeficiencyTypeSchema).optional(),
    narrative: z.string().max(4000).nullable().optional(),
});
export const AddCorrectionBodySchema = z.object({
    note: z.string().min(1).max(4000),
    photoStorageKey: z.string().max(1024).nullable().optional(),
});
export const GenerateUploadUrlBodySchema = z.object({
    filename: z.string().min(1).max(255),
    contentType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/heic']),
    kind: PhotoKindSchema,
    caption: z.string().max(500).nullable().optional(),
});
// ============================================================================
// RESPONSE
// ============================================================================
export const AcknowledgmentCoverageSchema = z.object({
    acknowledgedCount: z.number().int().min(0),
    totalExpected: z.number().int().min(0).nullable(),
});
export const WalkthroughSummaryResponseSchema = z.object({
    id: z.uuid(),
    leaseId: z.uuid(),
    unitId: z.uuid(),
    phase: WalkthroughPhaseSchema,
    actorRole: WalkthroughActorRoleSchema,
    mode: CaptureModeSchema,
    status: WalkthroughStatusSchema,
    submittedAt: z.string().nullable(),
    submittedByIdentityId: z.uuid().nullable(),
    itemCount: z.number().int().min(0),
    deficientCount: z.number().int().min(0),
    acknowledgedAt: z.string().nullable(),
    correctionCount: z.number().int().min(0),
    cursorItemId: z.uuid().nullable(),
    acknowledgmentCoverage: AcknowledgmentCoverageSchema,
});
export const ConditionItemPhotoResponseSchema = z.object({
    id: z.uuid(),
    kind: PhotoKindSchema,
    status: PhotoStatusSchema,
    thumbnailStorageKey: z.string().nullable(),
    displayOrder: z.number(),
});
export const ConditionItemResponseSchema = z.object({
    id: z.uuid(),
    walkthroughId: z.uuid(),
    unitRoomItemId: z.uuid().nullable(),
    roomName: z.string(),
    itemName: z.string(),
    category: ItemCategorySchema,
    condition: ItemConditionSchema,
    deficiencyTypes: z.array(DeficiencyTypeSchema),
    narrative: z.string().nullable(),
    displayOrder: z.number(),
    photoCount: z.number().int().min(0),
    photos: z.array(ConditionItemPhotoResponseSchema),
});
// BR-4a/8b: exactly one of `authorIdentityId`/`authorTokenId` is set (account author vs.
// accountless magic-link author) — never both, never a guessed person.
export const ConditionCorrectionResponseSchema = z.object({
    id: z.uuid(),
    conditionItemId: z.uuid(),
    authorRole: WalkthroughActorRoleSchema,
    authorIdentityId: z.uuid().nullable(),
    authorTokenId: z.uuid().nullable(),
    note: z.string(),
    photoStorageKey: z.string().nullable(),
    createdAt: z.string(),
});
export const ConditionPhotoResponseSchema = z.object({
    id: z.uuid(),
    conditionItemId: z.uuid(),
    storageKey: z.string(),
    thumbnailStorageKey: z.string().nullable(),
    status: PhotoStatusSchema,
    kind: PhotoKindSchema,
    caption: z.string().nullable(),
    displayOrder: z.number(),
    createdAt: z.string(),
});
export const GenerateUploadUrlResponseSchema = z.object({
    photoId: z.uuid(),
    uploadUrl: z.string(),
    storageKey: z.string(),
});
/** Manager (base) shape for `GET .../condition-walkthroughs?phase=&actorRole=`. */
export const GetForLeaseResponseSchema = z.object({ walkthrough: WalkthroughSummaryResponseSchema.nullable() });
/**
 * Manager shape of the lookup: for a non-owner (TENANT) DRAFT only the STATE is revealed
 * (BR-32 / BR-17) — `id` is null and no content counts are carried, so a DRAFT's id never
 * leaves the server. Every other walkthrough returns the full summary.
 */
export const DraftStatusOnlyResponseSchema = z.object({
    id: z.null(),
    leaseId: z.uuid(),
    phase: WalkthroughPhaseSchema,
    actorRole: WalkthroughActorRoleSchema,
    status: z.literal('DRAFT'),
});
export const ManagerGetForLeaseResponseSchema = z.object({
    walkthrough: z.union([WalkthroughSummaryResponseSchema, DraftStatusOnlyResponseSchema]).nullable(),
});
/** Tenant shape for the same endpoint — adds `leaseState` (see file header). */
export const TenantGetForLeaseResponseSchema = GetForLeaseResponseSchema.extend({
    leaseState: LeaseStateSchema,
});
export const CreateWalkthroughResponseSchema = z.object({ walkthrough: WalkthroughSummaryResponseSchema });
export const ResumeResponseSchema = z.object({ summary: WalkthroughSummaryResponseSchema, items: z.array(ConditionItemResponseSchema) });
export const MarkItemResponseSchema = z.object({ item: ConditionItemResponseSchema });
export const AddCorrectionResponseSchema = z.object({ correction: ConditionCorrectionResponseSchema });
/**
 * One reason a single item blocked submit — the `gaps` array entry of the 422
 * `WalkthroughIncompleteResponseSchema` (that wrapper schema itself stays route-local: it
 * extends apps/api's `ErrorResponseSchema`, which this zod-only package cannot import —
 * same precedent as `leasing-showings.routes.ts`'s local `UnprocessableEntityResponseSchema`).
 */
export const WalkthroughIncompleteGapSchema = z.object({
    itemId: z.uuid(),
    itemName: z.string(),
    reason: z.enum(['NOT_ASSESSED', 'MISSING_PHOTO', 'MISSING_DEFICIENCY_TYPE']),
});
//# sourceMappingURL=walkthrough.js.map